import { defineStore } from "pinia";
import { ref, watch } from "@vue/runtime-core";
import { useRouter } from "vue-router";

export const useBrandStore = defineStore("brand", () => {
  const router = useRouter();

  // État réactif du brand - PAS de valeur par défaut
  const currentBrand = ref<string | null>(null);
  const isInitialized = ref(false);

  // Initialiser le brand depuis le localStorage
  function initialize() {
    if (isInitialized.value) return;

    const storedBrand = localStorage.getItem("brand");
    if (storedBrand) {
      currentBrand.value = storedBrand;
    }

    isInitialized.value = true;
  }

  // Mettre à jour le brand
  function setBrand(brand: string | null) {
    currentBrand.value = brand;

    if (brand) {
      localStorage.setItem("brand", brand);
    } else {
      localStorage.removeItem("brand");
    }

    // Émettre un événement pour notifier tous les composants
    window.dispatchEvent(new CustomEvent("brand-changed", { detail: { brand } }));
  }

  // Mettre à jour le brand depuis les données utilisateur
  function updateFromUserData(groups: any[]) {
    if (!groups || groups.length === 0) {
      // Si l'utilisateur n'a pas de groupe, rediriger vers ChooseWorkspace
      setBrand(null);
      return false;
    }

    // Récupérer le brand actuel du localStorage
    const storedBrand = localStorage.getItem("brand");

    // Vérifier si le brand stocké est valide pour cet utilisateur
    const isValidBrand = groups.some((group) => group.slug === storedBrand);

    if (!storedBrand || !isValidBrand) {
      // Si pas de brand ou brand invalide, utiliser le premier groupe
      const firstGroup = groups[0];
      setBrand(firstGroup.slug);
    } else {
      // Le brand est valide, le garder
      currentBrand.value = storedBrand;
    }

    return true;
  }

  // Réinitialiser le brand (lors de la déconnexion)
  function reset() {
    currentBrand.value = null;
    localStorage.removeItem("brand");
    isInitialized.value = false;
  }

  // Recharger la page si nécessaire après changement de brand
  function reloadIfNeeded(previousBrand: string | null) {
    // Si on est sur une page d'édition de support, recharger
    const isEditPage =
      window.location.pathname.includes("/supports/") &&
      (window.location.pathname.includes("/affiche") ||
        window.location.pathname.includes("/banderole") ||
        window.location.pathname.includes("/depliant") ||
        window.location.pathname.includes("/flyer") ||
        window.location.pathname.includes("/emailing") ||
        window.location.pathname.includes("/reseaux"));

    if (isEditPage && previousBrand !== currentBrand.value) {
      // Notification de changement
      const brandNames: Record<string, string> = {
        hv: "Horizon Vert",
        valtra: "Valtra",
        fendt: "Fendt",
      };

      const brandName = brandNames[currentBrand.value || ""] || currentBrand.value;

      alert(
        `Workspace changé vers ${brandName}. La page va se recharger pour appliquer les changements.`
      );

      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }

  // Watcher pour détecter les changements et recharger si nécessaire
  watch(currentBrand, (newBrand: string | null, oldBrand: string | null) => {
    if (oldBrand !== null && newBrand !== oldBrand) {
      reloadIfNeeded(oldBrand);
    }
  });

  // Initialiser au chargement du store
  initialize();

  return {
    currentBrand,
    isInitialized,
    initialize,
    setBrand,
    updateFromUserData,
    reset,
    reloadIfNeeded,
  };
});
